import React from "react";
import LegalPage from "@/components/LegalPage";

/**
 * Terms & Conditions — public document, required for LinkedIn
 * Developer Portal app review and for general legal compliance.
 *
 * Plain language. Operationally accurate. No clickwrap nonsense.
 */
export default function TermsAndConditions() {
    return (
        <LegalPage
            title="Terms & Conditions"
            lastUpdated="18 February 2026"
            summary={
                "These terms govern your use of Spy Finance / Gary's Shed. The service is provided on a personal, non-commercial basis to the account-holder and any contractors they explicitly invite. There is no trial, no advertising tier, and no commercial reseller arrangement. Use of the service constitutes agreement to these terms."
            }
            sections={[
                {
                    heading: "The service",
                    body: [
                        "Spy Finance / Gary's Shed (\"the service\") is a private operational ledger and life-data system operated by the individual account-holder. It is provided as software-as-a-service over the public internet, hosted by Emergent.",
                        "Features include: a hash-chained life-events ledger; a Drive-based intake pipeline; an AI-assisted brief generator; contact management; weather, lunar, and circadian overlays; CV composition; payments processing for subscription tier upgrades; and other tools as they are added.",
                    ],
                },
                {
                    heading: "Account eligibility",
                    body: [
                        "You may register for an account if you are at least 18 years old and resident in a jurisdiction where the service is not prohibited by law. You agree to provide accurate registration information and to keep your password secure. You are responsible for all activity on your account.",
                    ],
                },
                {
                    heading: "Acceptable use",
                    body: [
                        "You agree not to use the service to: violate any applicable law; infringe the intellectual-property rights of others; harass, threaten, or impersonate any person; transmit malware or other hostile code; attempt to gain unauthorised access to other accounts or to the underlying infrastructure; reverse-engineer the service in pursuit of a competing product; or place a load on the service materially in excess of typical individual use.",
                        "We may suspend or terminate any account where these terms are breached.",
                    ],
                },
                {
                    heading: "Third-party integrations",
                    body: [
                        "The service integrates with third-party providers (Google Drive, Anthropic, Google AI, Stripe, Mureka, LinkedIn data exports). Your use of each integration is also governed by the relevant third party's terms. You are responsible for maintaining your accounts with those third parties and for any fees they charge you directly.",
                        "We may discontinue or substitute any integration at any time. Where reasonably practicable we will give 30 days' notice of a discontinuation.",
                    ],
                },
                {
                    heading: "Subscription tiers and payment",
                    body: [
                        "Some features require an active paid subscription. Subscriptions are billed via Stripe. Current pricing is displayed in-app on the Billing page. Prices are inclusive of VAT where applicable.",
                        "Subscriptions renew automatically until cancelled. You may cancel at any time via the Billing page; cancellation takes effect at the end of the current billing period and access to paid features remains until that date.",
                        "Refunds are at the operator's reasonable discretion. We will not unreasonably withhold a refund where a material service failure has occurred.",
                    ],
                },
                {
                    heading: "Your data",
                    body: [
                        "Your data remains yours. We do not claim ownership of the data you upload, generate, or otherwise place in the service. You grant us a limited, non-exclusive, non-transferable licence to host, store, transmit, display, and process your data solely as needed to provide the service and as separately described in our Privacy Policy.",
                        "You may export your data at any time via the in-app export functions. You may close your account at any time and request deletion of your data; see the Privacy Policy for the deletion procedure.",
                    ],
                },
                {
                    heading: "Service availability",
                    body: [
                        "We aim for high availability but do not guarantee uninterrupted service. Maintenance, third-party outages, and unforeseen incidents may cause temporary unavailability. We will publish material incidents in-app where practicable.",
                        "Scheduled maintenance, where required, is performed during off-peak hours and announced at least 24 hours in advance via the in-app banner.",
                    ],
                },
                {
                    heading: "Intellectual property",
                    body: [
                        "The service software, branding, copy, and visual design are the property of the operator. You are granted a limited, non-exclusive licence to use the service while these terms remain in force. You may not redistribute, sublicense, or commercially exploit the service or any part of it without prior written consent.",
                        "Content generated by the AI integrations on your behalf (briefs, classifications, summaries) belongs to you and is provided without warranty as to accuracy. You are responsible for verifying any AI-generated content before relying on it.",
                    ],
                },
                {
                    heading: "Disclaimers",
                    body: [
                        "The service is provided \"as is\" and \"as available.\" To the maximum extent permitted by law we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.",
                        "The service is not a substitute for professional financial, legal, medical, or other specialist advice. Any AI-generated content is for your information only and should not be relied upon for material decisions without independent verification.",
                    ],
                },
                {
                    heading: "Limitation of liability",
                    body: [
                        "To the maximum extent permitted by law, our aggregate liability arising out of or relating to the service is limited to the greater of (a) £100, or (b) the total fees you have paid to us in the 12 months immediately preceding the event giving rise to the claim.",
                        "We are not liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, data, business opportunity, or goodwill, whether based in contract, tort, negligence, or otherwise, even if we have been advised of the possibility of such damages.",
                        "Nothing in these terms limits or excludes liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded under English law.",
                    ],
                },
                {
                    heading: "Indemnity",
                    body: [
                        "You agree to indemnify and hold harmless the operator from any third-party claim arising out of your misuse of the service, your breach of these terms, or your infringement of any third party's rights.",
                    ],
                },
                {
                    heading: "Termination",
                    body: [
                        "You may terminate your account at any time via the Billing page. We may terminate your account on 30 days' notice, or immediately for material breach of these terms.",
                        "On termination, sections of these terms which by their nature should survive (intellectual property, disclaimers, limitation of liability, indemnity, governing law) will continue in force.",
                    ],
                },
                {
                    heading: "Changes to these terms",
                    body: [
                        "We may revise these terms from time to time. The version date at the top of this page is authoritative. Material changes will be announced in-app on next sign-in. Continued use of the service after the announcement constitutes acceptance of the revised terms.",
                    ],
                },
                {
                    heading: "Governing law",
                    body: [
                        "These terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over any dispute arising from or relating to these terms, except that you may bring a claim in the courts of your country of residence where required by mandatory consumer-protection law.",
                    ],
                },
                {
                    heading: "Contact",
                    body: [
                        "For all questions about these terms:",
                        "Email: legal@spyfinance.cloud",
                    ],
                },
            ]}
        />
    );
}
